/**
 * categoryPalette — a distinct colour per category, assigned by position.
 *
 * Used when the style editor fills in a field's values automatically: an operator
 * choosing a field with 80 values is not going to pick 80 colours by hand, so
 * every value arrives already styled and only the ones they care about get
 * adjusted.
 *
 * Two properties matter more than the specific hues:
 *
 *  1. **Stable.** The colour for index `n` never changes. Categories are merged
 *     into a style that is already in use, so a freshly discovered value must not
 *     recolour the ones an operator has already reviewed. That rules out picking
 *     colours at random per render, and it rules out fitting a ramp across "however
 *     many categories there are right now" — adding one value would shift them all.
 *  2. **Never duplicated.** `scaleOrdinal(schemeCategory10)` — the existing
 *     render-time `styleByField` path in `transformLayerConfigs.ts` — recycles
 *     after ten, so the 11th category is painted exactly like the 1st and the two
 *     are indistinguishable on the map. Past the scheme we generate hues instead
 *     of wrapping.
 *
 * The first ten colours ARE `schemeCategory10`, so tables styled this way keep the
 * look the app already has.
 */
import { schemeCategory10 } from "d3-scale-chromatic";

/** The named scheme used for the first {@link schemeCategory10}.length categories. */
const SCHEME: readonly string[] = schemeCategory10;

/**
 * Golden angle, in degrees. Successive multiples land far apart on the hue circle
 * and never repeat for any practical number of categories, which is what makes
 * this a better continuation than wrapping a ten-colour scheme.
 */
const GOLDEN_ANGLE = 137.508;

/**
 * Saturation/lightness pairs cycled through beyond the scheme, so two categories
 * whose hues happen to land close together still differ in depth.
 */
const TONES: ReadonlyArray<{ saturation: number; lightness: number }> = [
  { saturation: 0.65, lightness: 0.5 },
  { saturation: 0.45, lightness: 0.62 },
  { saturation: 0.75, lightness: 0.38 }
];

/** `#rrggbb` for an HSL triple, with h in degrees and s/l in 0–1. */
export function hslToHex(hue: number, saturation: number, lightness: number): string {
  const h = ((hue % 360) + 360) % 360;
  const s = Math.min(1, Math.max(0, saturation));
  const l = Math.min(1, Math.max(0, lightness));

  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const secondary = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const offset = l - chroma / 2;

  const [r, g, b] =
    h < 60
      ? [chroma, secondary, 0]
      : h < 120
        ? [secondary, chroma, 0]
        : h < 180
          ? [0, chroma, secondary]
          : h < 240
            ? [0, secondary, chroma]
            : h < 300
              ? [secondary, 0, chroma]
              : [chroma, 0, secondary];

  const channel = (value: number) =>
    Math.round((value + offset) * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/**
 * The colour for the category at `index`. Always a `#rrggbb` string — hex is what
 * the GeoStyler style stores, and the QGIS/SLD exporters and MapLibre paint
 * properties all read it without conversion.
 */
export function categoryColor(index: number): string {
  const i = Math.max(0, Math.floor(index));
  if (i < SCHEME.length) return SCHEME[i];

  const beyond = i - SCHEME.length;
  const tone = TONES[beyond % TONES.length];
  return hslToHex(beyond * GOLDEN_ANGLE, tone.saturation, tone.lightness);
}

/**
 * `count` colours starting at `startIndex` — the form the merge helper wants when
 * appending newly discovered values after the ones already styled.
 */
export function categoryColors(count: number, startIndex = 0): string[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => categoryColor(startIndex + i));
}
